import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchSubmissions } from '../api/submissionService';
import { fetchExams } from '../api/examService';
import { getScoreData, getInitials, getViolationColor, getScoreColor } from '../utils/format';
import { getBaseUrl } from '../api/client';
import Modal, { ModalHeader } from './common/Modal';
import Spinner from './common/Spinner';

const AdminPanel = () => {
  const [submissions, setSubmissions] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [subRes, examRes] = await Promise.all([
          fetchSubmissions(),
          fetchExams(),
        ]);
        setSubmissions(subRes);
        setExams(examRes);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyLink = useCallback((examId) => {
    const link = `https://online.kairaablockchainacademy.com/exam/${examId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(examId);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const openReview = useCallback((sub) => {
    let parsedAnswers = sub.answers;
    if (typeof parsedAnswers === 'string') {
      try { parsedAnswers = JSON.parse(parsedAnswers); } catch { parsedAnswers = []; }
    }
    setSelectedSub({ ...sub, answers: parsedAnswers });
  }, []);

  const closeModal = useCallback(() => setSelectedSub(null), []);

  if (loading) return <Spinner text="Loading dashboard..." />;

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Teacher Dashboard</h1>
          <p style={styles.subtitle}>Manage exams, share links, and review proctored submissions.</p>
        </div>
        <Link to="/admin/create" style={styles.createBtn}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >+ Create New Exam</Link>
      </div>

      <ExamLinksTable exams={exams} submissions={submissions} copiedId={copiedId} onCopy={copyLink} />
      <SubmissionsTable submissions={submissions} onReview={openReview} />

      {selectedSub && (
        <ReviewModal submission={selectedSub} onClose={closeModal} />
      )}
    </div>
  );
};

const ExamLinksTable = ({ exams, submissions, copiedId, onCopy }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>
      <span style={{ fontSize: 20 }}>📌</span>
      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111' }}>Exam Links</h3>
      <span style={{ fontSize: 12, color: '#aaa', marginLeft: 4 }}>Share these with students</span>
    </div>

    {exams.length === 0 ? (
      <div style={styles.emptyState}>
        No exams created yet. <Link to="/admin/create" style={{ color: '#4cbb4c' }}>Create one →</Link>
      </div>
    ) : (
      <table style={styles.table}>
        <thead>
          <tr>
            {['Exam Title', 'Questions', 'Duration', 'Submissions', 'Link'].map(h => (
              <th key={h} style={styles.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {exams.map(exam => {
            const subCount = submissions.filter(s => s.examId?._id === exam._id).length;
            return (
              <tr key={exam._id} style={styles.tr}>
                <td style={{ ...styles.td, fontWeight: 600, color: '#222' }}>{exam.title}</td>
                <td style={styles.td}>{exam.questions?.length || 0}</td>
                <td style={styles.td}>{exam.duration} min</td>
                <td style={styles.td}>
                  <span style={styles.countBadge}>{subCount}</span>
                </td>
                <td style={styles.td}>
                  <button onClick={() => onCopy(exam._id)} style={{
                    ...styles.copyBtn,
                    background: copiedId === exam._id ? '#2e7d32' : '#e8f5e9',
                    color: copiedId === exam._id ? '#fff' : '#2e7d32'
                  }}>
                    {copiedId === exam._id ? '✓ Copied!' : '📋 Copy Link'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

const SubmissionsTable = ({ submissions, onReview }) => (
  <div style={styles.card}>
    <div style={{ ...styles.cardTitle, marginBottom: 18 }}>
      <span style={{ fontSize: 20 }}>📝</span>
      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111' }}>Submitted Results</h3>
      <span style={styles.countBadge}>{submissions.length}</span>
    </div>

    {submissions.length === 0 ? (
      <div style={{ ...styles.emptyState, padding: '48px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
        No submissions yet. Share an exam link to get started.
      </div>
    ) : (
      <table style={styles.table}>
        <thead>
          <tr>
            {['Student Name', 'Exam', 'Score', 'Violations', 'Submitted At', 'Actions'].map(h => (
              <th key={h} style={styles.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {submissions.map(sub => {
            let parsedAnswers = sub.answers;
            if (typeof parsedAnswers === 'string') {
              try { parsedAnswers = JSON.parse(parsedAnswers); } catch { parsedAnswers = []; }
            }
            const score = getScoreData(parsedAnswers);
            const vColor = getViolationColor(sub.violations?.length || 0);

            return (
              <tr key={sub._id} style={styles.tr}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ ...styles.td, fontWeight: 600, color: '#222' }}>
                  <div style={styles.studentCell}>
                    <div style={styles.avatar}>{getInitials(sub.studentName)}</div>
                    {sub.studentName}
                  </div>
                </td>
                <td style={styles.td}>{sub.examId?.title || 'Unknown Exam'}</td>
                <td style={styles.td}>
                  {score ? (
                    <span style={{
                      ...styles.scoreBadge,
                      background: getScoreColor(score.percent).bg,
                      color: getScoreColor(score.percent).color
                    }}>
                      {score.correct}/{score.total} ({score.percent}%)
                    </span>
                  ) : (
                    <span style={{ color: '#bbb', fontSize: 13 }}>N/A</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.violationBadge,
                    background: vColor.bg,
                    color: vColor.color
                  }}>{vColor.label}</span>
                </td>
                <td style={{ ...styles.td, color: '#888', fontSize: 13 }}>
                  {new Date(sub.createdAt).toLocaleString()}
                </td>
                <td style={styles.td}>
                  <button onClick={() => onReview(sub)} style={styles.reviewBtn}
                    onMouseEnter={e => { e.currentTarget.style.background = '#1565c0'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#e3f2fd'; e.currentTarget.style.color = '#1565c0'; }}
                  >Review</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

const ReviewModal = ({ submission, onClose }) => {
  const score = getScoreData(submission.answers);
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const preVideoRef = useRef(null);
  const [preVideoError, setPreVideoError] = useState(false);
  const [preVideoKey, setPreVideoKey] = useState(0);

  const videoUrl = submission.videoFileName
    ? `${getBaseUrl()}/uploads/${submission.videoFileName}`
    : null;

  const preVideoUrl = submission.preExamVideoFileName
    ? `${getBaseUrl()}/uploads/${submission.preExamVideoFileName}`
    : null;

  const retryVideo = () => {
    setVideoError(false);
    setVideoKey(k => k + 1);
  };

  const retryPreVideo = () => {
    setPreVideoError(false);
    setPreVideoKey(k => k + 1);
  };

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader onClose={onClose}>
        <h3 style={{ margin: '0 0 2px', fontSize: 18, fontWeight: 800, color: '#111' }}>
          {submission.studentName}
        </h3>
        <p style={{ margin: 0, fontSize: 13, color: '#888' }}>
          {submission.examId?.title || 'Unknown Exam'} · Submitted {new Date(submission.createdAt).toLocaleString()}
        </p>
      </ModalHeader>

      <div style={styles.modalBody}>
        <div style={styles.modalLeft}>
          <div style={{ padding: 20, borderBottom: '1px solid #eee' }}>
            <h4 style={styles.sectionTitle}>📹 Exam Recording</h4>
            {videoUrl ? (
              <div>
                <video key={videoKey} ref={videoRef} controls preload="auto" playsInline
                  style={styles.videoPlayer}
                  onError={() => setVideoError(true)}
                >
                  <source src={videoUrl} type="video/webm" />
                </video>
                {videoError && (
                  <div style={{ marginTop: 8, textAlign: 'center' }}>
                    <p style={{ color: '#c62828', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
                      ⚠️ Video failed to load
                    </p>
                    <button onClick={retryVideo} style={{
                      padding: '6px 16px', borderRadius: 6, border: 'none',
                      background: '#1565c0', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer'
                    }}>Retry</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.noVideo}>No video recording found</div>
            )}
          </div>

          <div style={{ padding: 20, borderBottom: '1px solid #eee' }}>
            <h4 style={styles.sectionTitle}>📸 Pre-Exam Identity Verification</h4>
            {preVideoUrl ? (
              <div>
                <video key={preVideoKey} ref={preVideoRef} controls preload="auto" playsInline
                  style={styles.videoPlayer}
                  onError={() => setPreVideoError(true)}
                >
                  <source src={preVideoUrl} type="video/webm" />
                </video>
                {preVideoError && (
                  <div style={{ marginTop: 8, textAlign: 'center' }}>
                    <p style={{ color: '#c62828', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
                      ⚠️ Pre-exam video failed to load
                    </p>
                    <button onClick={retryPreVideo} style={{
                      padding: '6px 16px', borderRadius: 6, border: 'none',
                      background: '#1565c0', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer'
                    }}>Retry</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.noVideo}>No pre-exam verification video</div>
            )}
          </div>

          <div style={{ padding: 20, flex: 1 }}>
            <h4 style={styles.sectionTitle}>
              ⚠️ Violations ({submission.violations?.length || 0})
            </h4>
            {(!submission.violations || submission.violations.length === 0) ? (
              <div style={styles.cleanSession}>✓ Clean session — no violations detected</div>
            ) : (
              <div style={styles.violationList}>
                {submission.violations.map((v, i) => (
                  <div key={i} style={styles.violationCard}>
                    <div style={{ color: '#c62828', fontSize: 13, fontWeight: 600 }}>⚠️ {v.type}</div>
                    <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>
                      {new Date(v.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={styles.modalRight}>
          <div style={styles.scoreHeader}>
            <h4 style={styles.sectionTitle}>📊 Answer Review</h4>
            {score && (
              <div style={{
                ...styles.scoreBadge,
                background: getScoreColor(score.percent).bg,
                color: getScoreColor(score.percent).color
              }}>
                Score: {score.correct}/{score.total} ({score.percent}%)
              </div>
            )}
          </div>

          {(!submission.answers || submission.answers.length === 0) ? (
            <div style={styles.emptyState}>No answer data available.</div>
          ) : (
            submission.answers.map((ans, idx) => {
              const isUnanswered = ans.selectedOptionText === null;
              const isCorrect = ans.isCorrect === true;

              return (
                <div key={idx} style={{
                  ...styles.answerCard,
                  borderColor: isUnanswered ? '#eee' : isCorrect ? '#4cbb4c' : '#ff4c4c',
                  background: isUnanswered ? '#fafafa' : isCorrect ? '#f8fdf8' : '#fff8f8'
                }}>
                  <div style={styles.answerHeader}>
                    <span style={{
                      ...styles.aNumber,
                      background: isUnanswered ? '#eee' : isCorrect ? '#4cbb4c' : '#ff4c4c',
                      color: isUnanswered ? '#999' : '#fff'
                    }}>{idx + 1}</span>
                    <div style={{ flex: 1 }}>
                      <p style={styles.answerText}>{ans.questionText}</p>
                      {isUnanswered && (
                        <span style={{ fontSize: 12, color: '#c62828', fontWeight: 600 }}>
                          ⚠️ Skipped
                        </span>
                      )}
                    </div>
                    {!isUnanswered && (
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                        background: isCorrect ? '#e8f5e9' : '#fdecea',
                        color: isCorrect ? '#2e7d32' : '#c62828'
                      }}>
                        {isCorrect ? '✓ Correct' : '✗ Wrong'}
                      </span>
                    )}
                  </div>

                  <div style={styles.optContainer}>
                    {ans.options.map((opt, optIdx) => {
                      const isStudentPick = optIdx === ans.selectedOptionIndex;
                      const isCorrectOpt = optIdx === ans.correctOptionIndex;

                      let bg = '#fff', border = '#eee', leftBorder = '3px solid transparent', label = null;

                      if (isCorrectOpt && isStudentPick) {
                        bg = '#e8f5e9'; border = '#a5d6a7'; leftBorder = '3px solid #2e7d32';
                        label = <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: 12 }}>✓ Correct</span>;
                      } else if (isStudentPick && !isCorrectOpt) {
                        bg = '#fdecea'; border = '#ef9a9a'; leftBorder = '3px solid #c62828';
                        label = <span style={{ color: '#c62828', fontWeight: 700, fontSize: 12 }}>✗ Student picked</span>;
                      } else if (isCorrectOpt) {
                        bg = '#f1f8e9'; border = '#c5e1a5'; leftBorder = '3px solid #7cb342';
                        label = <span style={{ color: '#558b2f', fontWeight: 700, fontSize: 12 }}>✓ Correct answer</span>;
                      }

                      return (
                        <div key={optIdx} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 14px', borderRadius: 8,
                          background: bg, border: `1px solid ${border}`,
                          borderLeft: leftBorder, transition: 'all .15s'
                        }}>
                          <span style={{
                            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                            border: `2px solid ${isStudentPick ? '#1565c0' : '#ccc'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isStudentPick ? '#1565c0' : 'transparent'
                          }}>
                            {isStudentPick && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                          </span>
                          <span style={{ flex: 1, fontSize: 14, color: '#333' }}>{opt}</span>
                          {label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh', background: '#f5f5f5',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '32px 24px'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 32, flexWrap: 'wrap', gap: 16
  },
  title: { margin: '0 0 4px', fontSize: 26, fontWeight: 800, color: '#111' },
  subtitle: { margin: 0, color: '#888', fontSize: 14 },
  createBtn: {
    padding: '10px 24px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)', color: '#fff',
    fontWeight: 700, fontSize: 14, textDecoration: 'none', transition: 'all .25s'
  },
  card: {
    background: '#fff', borderRadius: 14, padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: 32
  },
  cardTitle: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 12,
    fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: 0.5, borderBottom: '2px solid #eee'
  },
  tr: { borderBottom: '1px solid #f0f0f0', transition: 'background .15s' },
  td: { padding: '14px', color: '#666' },
  countBadge: {
    background: '#f0f0f0', padding: '3px 10px', borderRadius: 12,
    fontSize: 13, fontWeight: 600, color: '#555'
  },
  copyBtn: {
    padding: '7px 16px', border: 'none', borderRadius: 8,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .2s'
  },
  emptyState: {
    textAlign: 'center', padding: '32px 0', color: '#bbb'
  },
  studentCell: {
    display: 'flex', alignItems: 'center', gap: 10
  },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 13, flexShrink: 0
  },
  scoreBadge: {
    padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700
  },
  violationBadge: {
    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700
  },
  reviewBtn: {
    padding: '8px 18px', border: 'none', borderRadius: 8,
    background: '#e3f2fd', color: '#1565c0', fontWeight: 700,
    fontSize: 13, cursor: 'pointer', transition: 'all .2s'
  },
  modalBody: {
    display: 'flex', gap: 0
  },
  modalLeft: {
    flex: '0 0 400px', borderRight: '1px solid #eee',
    display: 'flex', flexDirection: 'column'
  },
  modalRight: {
    flex: 1, padding: 20, overflowY: 'auto', maxHeight: '80vh'
  },
  sectionTitle: {
    margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#555',
    textTransform: 'uppercase', letterSpacing: 0.5
  },
  videoPlayer: {
    width: '100%', borderRadius: 10, background: '#000'
  },
  noVideo: {
    width: '100%', aspectRatio: '4/3', borderRadius: 10, background: '#1a1a1a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#666', fontSize: 14
  },
  cleanSession: {
    padding: '16px', background: '#e8f5e9', borderRadius: 10,
    color: '#2e7d32', fontSize: 14, fontWeight: 600, textAlign: 'center'
  },
  violationList: {
    maxHeight: 300, overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: 6
  },
  violationCard: {
    padding: '10px 12px', background: '#fff5f5', borderRadius: 8,
    borderLeft: '3px solid #ff4c4c'
  },
  scoreHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20
  },
  answerCard: {
    marginBottom: 20, borderRadius: 12, overflow: 'hidden',
    border: '2px solid #eee'
  },
  answerHeader: {
    padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start',
    borderBottom: '1px solid #f0f0f0'
  },
  aNumber: {
    minWidth: 28, height: 28, borderRadius: 7,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 800, flexShrink: 0
  },
  answerText: {
    margin: 0, fontSize: 15, lineHeight: 1.5, color: '#222', fontWeight: 500
  },
  optContainer: {
    padding: '12px 16px 14px 56px',
    display: 'flex', flexDirection: 'column', gap: 6
  }
};

export default AdminPanel;
